import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, Compass, FileText, ArrowRight, Camera, Trash2, Pencil, Check, Loader2, ShieldAlert, ClipboardList } from "lucide-react";
import client, { detectOrgSlug, fetchCobrand, fetchProfilePhotoUrl } from "../lib/api";

/**
 * Profile. A calm read of who you are on this portal: your name, your
 * photo, your product, and your college if you are on a college seat.
 * The photo you set here also lands on your resume. You can edit your
 * name and date of birth; changing the date re-derives minor status.
 */
export default function ProfileScreen({ product }) {
  const base = product === "grow" ? "/grow" : "";
  const [me, setMe] = useState(null);
  const [cobrand, setCobrand] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);

  // Edit profile (KLA-010)
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDob, setEditDob] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [saveErr, setSaveErr] = useState("");
  const [becameMinor, setBecameMinor] = useState(false);

  const loadProfile = () => {
    client.get("/profile").then((r) => {
      setProfile(r.data);
      setEditName(r.data.display_name || "");
      setEditDob(r.data.date_of_birth || "");
    }).catch(() => setProfile(null));
  };

  useEffect(() => {
    client.get("/consent/status").then((r) => setMe(r?.data?.person || {})).catch(() => setMe({}));
    loadProfile();
    const slug = detectOrgSlug();
    if (slug) fetchCobrand(slug).then(setCobrand).catch(() => setCobrand(null));
    fetchProfilePhotoUrl().then(setPhotoUrl);
    return () => setPhotoUrl((u) => { if (u) URL.revokeObjectURL(u); return null; });
  }, []);

  const name = editName || me?.display_name || profile?.display_name || "You";

  const saveProfile = async () => {
    setSaving(true); setSaveErr(""); setSaveMsg(""); setBecameMinor(false);
    try {
      const body = {};
      body.display_name = editName.trim();
      if (editDob) body.date_of_birth = editDob;
      const { data } = await client.put("/profile", body);
      setProfile((p) => ({ ...(p || {}), display_name: data.display_name, date_of_birth: data.date_of_birth, is_minor: data.is_minor }));
      setMe((m) => ({ ...(m || {}), display_name: data.display_name, is_minor: data.is_minor }));
      setBecameMinor(!!data.became_minor);
      setSaveMsg("Saved.");
      setEditing(false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setSaveErr(typeof d === "string" ? d : "Could not save. Check the details and try again.");
    } finally {
      setSaving(false);
    }
  };

  const onPick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr("");
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await client.post("/profile/photo", form, { headers: { "Content-Type": "multipart/form-data" } });
      const fresh = await fetchProfilePhotoUrl();
      setPhotoUrl((old) => { if (old) URL.revokeObjectURL(old); return fresh; });
    } catch (_e) {
      setErr(_e?.response?.data?.detail || "That photo did not upload. Try a png or jpg under 5 MB.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removePhoto = async () => {
    setUploading(true);
    try {
      await client.delete("/profile/photo");
      setPhotoUrl((old) => { if (old) URL.revokeObjectURL(old); return null; });
    } catch (_e) { /* ignore */ } finally { setUploading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-5 pt-8 pb-24">
      <div className="k-label mb-2">account</div>
      <h1 className="klareet-serif mb-6" style={{ fontSize: "clamp(1.8rem, 4vw, 2.4rem)", lineHeight: 1.1 }} data-testid="profile-title">
        Your profile.
      </h1>

      <div className="k-card mb-5" data-testid="profile-card">
        <div className="flex items-center gap-4">
          <div className="relative shrink-0">
            {photoUrl ? (
              <img src={photoUrl} alt={name} className="w-16 h-16 rounded-2xl object-cover" data-testid="profile-photo" />
            ) : (
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-2xl font-semibold" style={{ background: "hsl(var(--klareet-ink))", color: "white" }} data-testid="profile-photo-fallback">
                {name.slice(0, 1).toUpperCase()}
              </div>
            )}
            <button
              onClick={onPick}
              disabled={uploading}
              className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full flex items-center justify-center shadow"
              style={{ background: "hsl(var(--klareet-teal))", color: "white" }}
              aria-label="change photo"
              title="Change photo"
              data-testid="profile-photo-upload-btn"
            >
              <Camera size={13} />
            </button>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onFile} data-testid="profile-photo-input" />
          </div>
          <div className="min-w-0">
            <div className="klareet-serif" style={{ fontSize: "1.4rem", color: "hsl(var(--klareet-ink))" }} data-testid="profile-name">{name}</div>
            <div className="k-quiet !text-sm">Klareet {product === "grow" ? "Grow" : "Start"} · your read is private to you</div>
            <div className="mt-2 flex items-center gap-3">
              <button onClick={onPick} disabled={uploading} className="k-btn k-btn-ghost !py-1 !px-2 !text-xs" data-testid="profile-photo-change">
                <Camera size={12} /> {uploading ? "Uploading" : photoUrl ? "Change photo" : "Add a photo"}
              </button>
              {photoUrl && (
                <button onClick={removePhoto} disabled={uploading} className="k-btn k-btn-ghost !py-1 !px-2 !text-xs" data-testid="profile-photo-remove">
                  <Trash2 size={12} /> Remove
                </button>
              )}
            </div>
            {err && <div className="text-xs mt-1" style={{ color: "hsl(var(--destructive))" }} data-testid="profile-photo-error">{err}</div>}
            <div className="k-quiet !text-xs mt-1">Your photo shows up on your resume too.</div>
          </div>
        </div>
      </div>

      {/* Edit profile (KLA-010) */}
      <div className="k-card mb-5" data-testid="profile-edit-card">
        <div className="flex items-center justify-between mb-3">
          <div className="k-label !mb-0 flex items-center gap-2"><Pencil size={13} /> your details</div>
          {!editing && (
            <button onClick={() => { setEditing(true); setSaveMsg(""); setSaveErr(""); }} className="k-btn k-btn-ghost !py-1 !px-2 !text-xs" data-testid="profile-edit-btn">
              <Pencil size={12} /> Edit
            </button>
          )}
        </div>

        {!editing ? (
          <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
            <div>
              <dt className="k-quiet !text-xs uppercase tracking-wide mb-0.5">name</dt>
              <dd className="text-sm font-medium" style={{ color: "hsl(var(--klareet-ink))" }} data-testid="profile-view-name">{profile?.display_name || "Not set"}</dd>
            </div>
            <div>
              <dt className="k-quiet !text-xs uppercase tracking-wide mb-0.5">date of birth</dt>
              <dd className="text-sm font-medium" style={{ color: "hsl(var(--klareet-ink))" }} data-testid="profile-view-dob">{profile?.date_of_birth || "Not set"}</dd>
            </div>
          </dl>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="k-label mb-1 block">name</label>
              <input className="k-input" value={editName} maxLength={120} onChange={(e) => setEditName(e.target.value)} placeholder="what should Klair call you" data-testid="profile-name-input" />
            </div>
            <div>
              <label className="k-label mb-1 block">date of birth</label>
              <input type="date" className="k-input" value={editDob} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setEditDob(e.target.value)} data-testid="profile-dob-input" style={{ maxWidth: "16rem" }} />
              <p className="k-quiet !text-xs mt-1">Changing this updates your age and, if you are under 18, adds the guardian step before your next check in.</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={saveProfile} disabled={saving} className="k-btn k-btn-primary" data-testid="profile-save-btn">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} {saving ? "Saving" : "Save"}
              </button>
              <button onClick={() => { setEditing(false); setEditName(profile?.display_name || ""); setEditDob(profile?.date_of_birth || ""); }} disabled={saving} className="k-btn k-btn-ghost" data-testid="profile-cancel-btn">Cancel</button>
            </div>
          </div>
        )}

        {saveErr && <div className="text-sm mt-2" style={{ color: "hsl(var(--destructive))" }} data-testid="profile-save-error">{saveErr}</div>}
        {saveMsg && <div className="text-sm mt-2 flex items-center gap-1.5" style={{ color: "hsl(var(--klareet-teal))" }} data-testid="profile-save-success"><Check size={14} /> {saveMsg}</div>}
        {becameMinor && (
          <div className="k-warm-card p-3 mt-3 text-sm flex items-start gap-2" data-testid="profile-minor-notice">
            <ShieldAlert size={16} style={{ color: "hsl(38 92% 40%)" }} className="mt-0.5 shrink-0" />
            <span>Your date of birth now reads as under 18. A guardian will need to give consent before your next check in with Klair.</span>
          </div>
        )}
      </div>

      {cobrand && (
        <div className="k-card mb-5" data-testid="profile-college">
          <div className="k-label mb-2 flex items-center gap-2"><GraduationCap size={14} /> your college</div>
          <div className="flex items-center gap-3">
            {cobrand.logo_url ? (
              <img src={cobrand.logo_url} alt={cobrand.display_name} className="h-8 w-8 object-contain rounded" />
            ) : (
              <div className="h-8 w-8 rounded flex items-center justify-center text-sm font-semibold" style={{ background: "hsl(var(--klareet-teal))", color: "white" }}>
                {(cobrand.display_name || "C").slice(0, 1).toUpperCase()}
              </div>
            )}
            <div className="font-medium" style={{ color: "hsl(var(--klareet-ink))" }}>{cobrand.display_name}</div>
            <Link to="/college" className="k-btn-outline !py-1.5 !px-3 text-xs ml-auto" data-testid="profile-college-link">
              About your college <ArrowRight size={12} />
            </Link>
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        <Link to={`${base}/account/answers`} className="k-card k-lift flex items-center gap-3" data-testid="profile-link-answers">
          <ClipboardList size={18} style={{ color: "hsl(var(--klareet-teal))" }} />
          <div><div className="font-medium" style={{ color: "hsl(var(--klareet-ink))" }}>Your answers</div><div className="k-quiet !text-xs">what you told Klair</div></div>
        </Link>
        <Link to={`${base}/report`} className="k-card k-lift flex items-center gap-3" data-testid="profile-link-snapshot">
          <Compass size={18} style={{ color: "hsl(var(--klareet-teal))" }} />
          <div><div className="font-medium" style={{ color: "hsl(var(--klareet-ink))" }}>My Snapshot</div><div className="k-quiet !text-xs">what your answers say</div></div>
        </Link>
        <Link to={`${base}/resume`} className="k-card k-lift flex items-center gap-3" data-testid="profile-link-resume">
          <FileText size={18} style={{ color: "hsl(var(--klareet-teal))" }} />
          <div><div className="font-medium" style={{ color: "hsl(var(--klareet-ink))" }}>Resume</div><div className="k-quiet !text-xs">build and share</div></div>
        </Link>
      </div>
    </div>
  );
}
